// Story 056 — Histogram widget state: open/closed + channel overlays +
// log-scale, persisted to localStorage under the story-pinned key
// `aero.lyse.analysis.histogram.v1`.
//
// Visibility follows the SB6-ANA-FILT-06 on-demand-widget pattern
// (`advancedFilterWidgetStore`): the workspace grid reconciles against
// `open` and watches `openTicket` to place the widget into the first
// empty region on each explicit open; the Settings → Layout & Widgets
// checkbox and the widget header's ✕ both drive this store, so there is
// exactly ONE visibility mechanism. Unlike the advanced filter widgets
// the open flag persists (story Scope: "widget open / closed state and
// per-channel toggle state persist"), which is why the whole envelope
// lives in this store's localStorage key rather than the per-user
// `aero.workspace.hidden.*` map (that map defaults ABSENT keys to
// visible — the wrong default for a widget that must start closed).
//
// Multi-window note: each window (main + pop-outs) holds its own module
// instance of this store. Persistence is therefore READ-MODIFY-WRITE per
// mutation (`persistPatch`) — a window writes only the field it changed
// on top of whatever is currently stored, so a `setOpen` in the main
// window can never clobber a `logScale` flipped in a pop-out.
import { create } from "zustand";
import {
  usePopoutWorkspaceStore,
  outWidgetsOf,
} from "../multiwindow/popoutWorkspaceStore.js";

export const HISTOGRAM_STORAGE_KEY = "aero.lyse.analysis.histogram.v1";

export type HistogramChannel = "r" | "g" | "b";

export interface HistogramChannels {
  r: boolean;
  g: boolean;
  b: boolean;
}

interface PersistedHistogramState {
  open: boolean;
  channels: HistogramChannels;
  logScale: boolean;
}

function defaults(): PersistedHistogramState {
  return {
    open: false, // story Open Q2 — closed on first visit
    channels: { r: false, g: false, b: false }, // luminance-only default
    logScale: false,
  };
}

/** Lenient parse — garbage in, defaults out; partials fill with defaults. */
export function parsePersistedHistogramState(
  raw: unknown,
): PersistedHistogramState {
  const base = defaults();
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return base;
  }
  const rec = raw as Record<string, unknown>;
  const channels =
    typeof rec.channels === "object" && rec.channels !== null
      ? (rec.channels as Record<string, unknown>)
      : {};
  return {
    open: typeof rec.open === "boolean" ? rec.open : base.open,
    channels: {
      r: typeof channels.r === "boolean" ? channels.r : base.channels.r,
      g: typeof channels.g === "boolean" ? channels.g : base.channels.g,
      b: typeof channels.b === "boolean" ? channels.b : base.channels.b,
    },
    logScale: typeof rec.logScale === "boolean" ? rec.logScale : base.logScale,
  };
}

function load(): PersistedHistogramState {
  try {
    const raw = window.localStorage.getItem(HISTOGRAM_STORAGE_KEY);
    if (raw === null) return defaults();
    return parsePersistedHistogramState(JSON.parse(raw) as unknown);
  } catch {
    // Corrupt JSON / storage denied — defaults, never a crash.
    return defaults();
  }
}

interface HistogramPersistPatch {
  open?: boolean;
  channels?: Partial<HistogramChannels>;
  logScale?: boolean;
}

/**
 * Read-modify-write persistence: merge the patch onto the CURRENTLY
 * STORED envelope (not this window's in-memory copy), so concurrent
 * windows only ever overwrite the fields they actually changed.
 */
function persistPatch(patch: HistogramPersistPatch): void {
  try {
    const current = load();
    const merged: PersistedHistogramState = {
      open: patch.open ?? current.open,
      logScale: patch.logScale ?? current.logScale,
      channels: patch.channels
        ? { ...current.channels, ...patch.channels }
        : current.channels,
    };
    window.localStorage.setItem(HISTOGRAM_STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // Quota or private mode — fail silently.
  }
}

interface HistogramWidgetState extends PersistedHistogramState {
  /** Bumped on every open (false→true) so the workspace can place the
   *  widget into the first empty grid region — mirrors the
   *  `advancedFilterWidgetStore.openTicket` contract. */
  openTicket: number;
  /**
   * The `openTicket` value the workspace has already PLACED. Lives in the
   * store (module singleton, survives workspace remounts) rather than a
   * per-mount ref inside `AnalysisWorkspace` — a ref would reset to 0 on
   * every navigation back into the analysis surface, making the placement
   * effect re-fire and yank the widget away from wherever the user
   * dragged it. Placement must happen exactly once per open ACTION.
   */
  placedTicket: number;
  setOpen: (open: boolean) => void;
  /** Workspace acknowledgment: `openTicket` has been placed on the grid. */
  markPlaced: (ticket: number) => void;
  toggleChannel: (channel: HistogramChannel) => void;
  setLogScale: (logScale: boolean) => void;
}

export const useHistogramWidgetStore = create<HistogramWidgetState>((set) => ({
  ...load(),
  openTicket: 0,
  placedTicket: 0,
  setOpen: (open) => {
    if (!open) {
      // Close-while-popped: match how poppable widgets leave a pop-out —
      // `unassign` returns the widget to the main workspace and the
      // now-empty window self-closes (PopoutHost watches its record).
      // Without this, unchecking "Show Histogram" would orphan a pop-out
      // window showing a widget the workspace considers closed.
      const popout = usePopoutWorkspaceStore.getState();
      if (outWidgetsOf(popout.windows).has("histogram")) {
        popout.unassign("histogram");
      }
    }
    persistPatch({ open });
    set((s) => (open ? { open, openTicket: s.openTicket + 1 } : { open }));
  },
  markPlaced: (ticket) => {
    set({ placedTicket: ticket });
  },
  toggleChannel: (channel) => {
    set((s) => {
      const value = !s.channels[channel];
      persistPatch({ channels: { [channel]: value } });
      return { channels: { ...s.channels, [channel]: value } };
    });
  },
  setLogScale: (logScale) => {
    persistPatch({ logScale });
    set({ logScale });
  },
}));
